import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";

const STAGES = ["New", "Qualification", "Proposal", "Negotiation", "Won", "Lost"];

const LeadConversionModal = ({
  isOpen,
  onClose,
  leadData,
  conversionInfo,
  onConfirm,
  isSubmitting,
}) => {
  const [accountAction, setAccountAction] = useState("use_existing"); // "use_existing" | "create_new"
  const [opportunityAction, setOpportunityAction] = useState("use_existing"); // "use_existing" | "create_new" | "none"
  const [selectedOppId, setSelectedOppId] = useState("");
  const [oppTitle, setOppTitle] = useState("");
  const [oppAmount, setOppAmount] = useState("");
  const [oppStage, setOppStage] = useState("New");

  const hasExistingAccount = Boolean(conversionInfo?.has_existing_account && conversionInfo?.existing_account);
  const existingAccount = conversionInfo?.existing_account;
  const hasExistingOpp = Boolean(
    conversionInfo?.has_existing_opportunity &&
    conversionInfo?.existing_opportunities?.length > 0
  );
  const existingOpps = conversionInfo?.existing_opportunities || [];

  useEffect(() => {
    if (isOpen) {
      setAccountAction(hasExistingAccount ? "use_existing" : "create_new");
      setOpportunityAction(hasExistingOpp ? "use_existing" : "none");
      if (hasExistingOpp && existingOpps.length > 0) {
        setSelectedOppId(existingOpps[0]?.id || "");
      }
      const defaultOppTitle =
        (leadData?.company_name || leadData?.lead_name || leadData?.first_name || "Lead") +
        " - Deal";
      setOppTitle(defaultOppTitle);
      setOppAmount("");
      setOppStage("New");
    }
  }, [isOpen, hasExistingAccount, hasExistingOpp, leadData]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      lead_status: "Convert Account",
      account_action: accountAction,
      existing_account_id: accountAction === "use_existing" ? existingAccount?.id : null,
      opportunity_action: opportunityAction,
      existing_opportunity_id:
        opportunityAction === "use_existing" ? selectedOppId : null,
      opportunity_title:
        opportunityAction === "create_new" ? oppTitle : null,
      opportunity_amount:
        opportunityAction === "create_new" && oppAmount ? Number(oppAmount) : null,
      opportunity_stage:
        opportunityAction === "create_new" ? oppStage : null,
    };
    onConfirm(payload);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "580px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827" }}>
            Convert Lead to Account
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "22px",
              lineHeight: 1,
              cursor: "pointer",
              color: "#9CA3AF",
            }}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} style={{ overflowY: "auto", padding: "20px 24px" }}>
          {/* Account Section */}
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Account Details
            </div>

            {hasExistingAccount ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Option 1: Use existing */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    border: `1.5px solid ${accountAction === "use_existing" ? "#6B46FE" : "#E5E7EB"}`,
                    backgroundColor: accountAction === "use_existing" ? "#F5F3FF" : "#FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="accountAction"
                    value="use_existing"
                    checked={accountAction === "use_existing"}
                    onChange={() => setAccountAction("use_existing")}
                    style={{ marginTop: "4px", accentColor: "#6B46FE" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#1F2937", fontSize: "14px" }}>
                      Use existing details
                    </div>
                    <div style={{ fontSize: "13px", color: "#4B5563", marginTop: "4px" }}>
                      <strong>{existingAccount.company_name || existingAccount.first_name}</strong>
                      {existingAccount.code && <span> ({existingAccount.code})</span>}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
                      Email: {existingAccount.email || "--"} &bull; Phone: {existingAccount.phonenumber || "--"}
                    </div>
                  </div>
                </label>

                {/* Option 2: Create new */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    border: `1.5px solid ${accountAction === "create_new" ? "#6B46FE" : "#E5E7EB"}`,
                    backgroundColor: accountAction === "create_new" ? "#F5F3FF" : "#FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="accountAction"
                    value="create_new"
                    checked={accountAction === "create_new"}
                    onChange={() => setAccountAction("create_new")}
                    style={{ marginTop: "4px", accentColor: "#6B46FE" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#1F2937", fontSize: "14px" }}>
                      Create new details with the entered information
                    </div>
                    <div style={{ fontSize: "13px", color: "#4B5563", marginTop: "4px" }}>
                      Will create a new Account for:{" "}
                      <strong>
                        {leadData?.company_name || leadData?.lead_name || leadData?.first_name || "--"}
                      </strong>
                    </div>
                  </div>
                </label>
              </div>
            ) : (
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: "8px",
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  fontSize: "13px",
                  color: "#374151",
                }}
              >
                No existing matching account found. A new Account will be created for:{" "}
                <strong>
                  {leadData?.company_name || leadData?.lead_name || leadData?.first_name || "--"}
                </strong>
              </div>
            )}
          </div>

          {/* Opportunity Section */}
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Opportunity Details
            </div>

            {hasExistingOpp ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {/* Option 1: Use existing */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    border: `1.5px solid ${opportunityAction === "use_existing" ? "#6B46FE" : "#E5E7EB"}`,
                    backgroundColor: opportunityAction === "use_existing" ? "#F5F3FF" : "#FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="opportunityAction"
                    value="use_existing"
                    checked={opportunityAction === "use_existing"}
                    onChange={() => setOpportunityAction("use_existing")}
                    style={{ marginTop: "4px", accentColor: "#6B46FE" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#1F2937", fontSize: "14px" }}>
                      Use existing details
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                      Link existing opportunities for this lead/account:
                    </div>
                    {existingOpps.length > 1 ? (
                      <select
                        value={selectedOppId}
                        onChange={(e) => setSelectedOppId(e.target.value)}
                        disabled={opportunityAction !== "use_existing"}
                        style={{
                          marginTop: "8px",
                          width: "100%",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #D1D5DB",
                          fontSize: "13px",
                        }}
                      >
                        {existingOpps.map((opp) => (
                          <option key={opp.id} value={opp.id}>
                            {opp.title} ({opp.opportunity_code || opp.stage}) - ₹{opp.amount || 0}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ fontSize: "13px", color: "#374151", marginTop: "4px" }}>
                        <strong>{existingOpps[0]?.title}</strong> &bull; Stage: {existingOpps[0]?.stage || "New"} &bull; ₹{existingOpps[0]?.amount || 0}
                      </div>
                    )}
                  </div>
                </label>

                {/* Option 2: Create new */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    border: `1.5px solid ${opportunityAction === "create_new" ? "#6B46FE" : "#E5E7EB"}`,
                    backgroundColor: opportunityAction === "create_new" ? "#F5F3FF" : "#FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="opportunityAction"
                    value="create_new"
                    checked={opportunityAction === "create_new"}
                    onChange={() => setOpportunityAction("create_new")}
                    style={{ marginTop: "4px", accentColor: "#6B46FE" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#1F2937", fontSize: "14px" }}>
                      Create new details with the entered information
                    </div>
                  </div>
                </label>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                  <input
                    type="checkbox"
                    checked={opportunityAction === "create_new"}
                    onChange={(e) => setOpportunityAction(e.target.checked ? "create_new" : "none")}
                    style={{ accentColor: "#6B46FE", width: "16px", height: "16px" }}
                  />
                  <span style={{ fontWeight: "500", color: "#374151" }}>
                    Create a new Opportunity for this Account
                  </span>
                </label>
              </div>
            )}

            {/* If Create New is selected for Opportunity */}
            {opportunityAction === "create_new" && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "14px",
                  borderRadius: "8px",
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#4B5563", marginBottom: "4px", display: "block" }}>
                    Opportunity Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={oppTitle}
                    onChange={(e) => setOppTitle(e.target.value)}
                    placeholder="e.g. Acme Corp - Annual Subscription"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #D1D5DB",
                      fontSize: "13px",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "12px", fontWeight: "600", color: "#4B5563", marginBottom: "4px", display: "block" }}>
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={oppAmount}
                      onChange={(e) => setOppAmount(e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #D1D5DB",
                        fontSize: "13px",
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "12px", fontWeight: "600", color: "#4B5563", marginBottom: "4px", display: "block" }}>
                      Stage
                    </label>
                    <select
                      value={oppStage}
                      onChange={(e) => setOppStage(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #D1D5DB",
                        fontSize: "13px",
                      }}
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
              paddingTop: "16px",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "8px 20px",
                borderRadius: "100px",
                border: "1px solid #D1D5DB",
                background: "#ffffff",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: "8px 24px",
                borderRadius: "100px",
                border: "none",
                background: "#6B46FE",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              {isSubmitting && <Spinner size="sm" animation="border" />}
              {isSubmitting ? "Converting..." : "Convert"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadConversionModal;
